<template>
  <div :class="rootClasses">
    <PageHeader
      v-if="!embedded"
      title="Contact"
      description="Have a project in mind? Let's talk about it."
      :breadcrumbs="[{ label: 'Home', to: '/' }, { label: 'Contact' }]"
    />
    <SectionHeader
      v-else
      title="Contact"
      description="Have a project in mind? Let's talk about it."
    />

    <div class="grid lg:grid-cols-5 gap-8">
      <div class="lg:col-span-2 space-y-6">
        <BaseCard v-for="item in contactInfo" :key="item.label" hover>
          <div class="flex items-start gap-4">
            <div class="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-muted text-primary shrink-0">
              <AppIcon :name="item.icon" :size="20" />
            </div>
            <div>
              <p class="text-label">{{ item.label }}</p>
              <p class="text-caption mt-0.5">{{ item.value }}</p>
            </div>
          </div>
        </BaseCard>

        <BaseCard title="Location" :padding="false" class="overflow-hidden">
          <iframe
            class="w-full h-64 border-0"
            title="Map showing Bagong Buhay, Victoria, Oriental Mindoro"
            :src="mapUrl"
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          />
        </BaseCard>
      </div>

      <BaseCard class="lg:col-span-3" title="Send a Message" description="I'll get back to you as soon as possible.">
        <form class="space-y-5 mt-2" @submit.prevent="submit">
          <div class="grid sm:grid-cols-2 gap-5">
            <BaseInput
              v-model="form.name"
              label="Your Name"
              placeholder="John Doe"
              :error="errors.name"
              required
              :autofocus="!embedded"
              @update:modelValue="clearErrors('name')"
            />
            <BaseInput
              v-model="form.email"
              label="Your Email"
              type="email"
              placeholder="you@example.com"
              :error="errors.email"
              required
              @update:modelValue="clearErrors('email')"
            />
          </div>
          <BaseInput
            v-model="form.subject"
            label="Subject"
            placeholder="Project inquiry"
            :error="errors.subject"
            required
            @update:modelValue="clearErrors('subject')"
          />
          <BaseTextarea
            v-model="form.message"
            label="Message"
            placeholder="Tell me about your project..."
            :rows="6"
            :error="errors.message"
            required
            @update:modelValue="clearErrors('message')"
          />
          <div class="flex justify-end gap-3 pt-2">
            <BaseButton variant="secondary" type="button" @click="clearForm">Clear</BaseButton>
            <BaseButton variant="primary" type="submit" :loading="isLoading">
              <AppIcon name="Send" :size="16" />
              Send Message
            </BaseButton>
          </div>
        </form>
      </BaseCard>
    </div>
  </div>
</template>

<script>
import { firebase } from '@/api/firebase'
import { siteState } from '@/composables/useSiteData'
import { useToast } from '@/composables/useToast'
import PageHeader from '@/components/layout/PageHeader.vue'
import SectionHeader from '@/components/layout/SectionHeader.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseTextarea from '@/components/ui/BaseTextarea.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'

export default {
  name: 'ContactPage',
  components: { PageHeader, SectionHeader, BaseCard, BaseInput, BaseTextarea, BaseButton, AppIcon },
  props: {
    embedded: Boolean,
  },
  computed: {
    rootClasses() {
      return this.embedded
        ? 'page-container py-16 lg:py-20 animate-fade-in'
        : 'page-container animate-fade-in'
    },
    mapUrl() {
      return siteState.contactMapUrl
    },
    contactInfo() {
      const info = siteState.profile.informations
      return [
        { label: 'Location', value: info.homeaddress, icon: 'MapPin' },
        { label: 'Email', value: info.email, icon: 'Mail' },
        { label: 'Phone', value: info.contact, icon: 'Phone' },
      ]
    },
  },
  data() {
    return {
      errors: {},
      isLoading: false,
      form: { name: '', email: '', subject: '', message: '' },
    }
  },
  methods: {
    validateEmail(email) {
      return String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    },
    getCurrentDate(format) {
      const date = new Date()
      const m = (date.getMonth() + 1).toString().padStart(2, '0')
      const y = date.getFullYear()
      const d = date.getDate().toString().padStart(2, '0')
      if (format == 'dd-mm-yyyy') return d + '-' + m + '-' + y
    },
    clearForm() {
      this.form = { name: '', email: '', subject: '', message: '' }
      this.errors = {}
    },
    async submit() {
      this.required(this.form)
      this.isLoading = true
      let isValid = true
      const keys = Object.keys(this.errors)
      for (const key of keys) {
        if (this.errors[key]) isValid = false
      }

      if (isValid) {
        const id = (await firebase.getValue('props/message_id')) ?? 1
        const payload = { ...this.form, date: this.getCurrentDate('dd-mm-yyyy') }
        const isSave = await firebase.insert(`messages/${parseInt(id) + 1}`, payload)
        if (isSave) {
          await firebase.update_value('props', { message_id: id + 1 })
          const { success } = useToast()
          success('Message sent!', 'Thank you for reaching out. I will get back to you soon.')
          this.clearForm()
        }
      }
      this.isLoading = false
    },
    clearErrors(field) {
      this.errors[field] = null
    },
    required(form) {
      const keys = Object.keys(form)
      for (const key of keys) {
        if (!form[key]) {
          this.errors[key] = 'Required field'
        } else if (key == 'email' && form[key]) {
          this.errors[key] = !this.validateEmail(form[key]) ? 'Invalid Email' : null
        }
      }
    },
  },
}
</script>
